import { Button, Checkbox, Text, toast } from "@medusajs/ui"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRef, useState } from "react"
import { errorMessage, sdk } from "@/lib/sdk"

type Issue = { line: number; email?: string; message: string }
type Result = { dry_run: boolean; rows: number; created: number; updated: number; skipped: number; errors: Issue[]; warnings: Issue[] }
const MAX_BYTES = 6_000_000
const TEMPLATE = "First Name,Last Name,Email,Accepts Email Marketing,Default Address Company,Default Address Address1,Default Address Address2,Default Address City,Default Address Province Code,Default Address Country Code,Default Address Zip,Phone,Tags,Note\nJane,Doe,jane@example.com,yes,,1 Main St,,Austin,TX,US,78701,+15125550100,VIP,\n"

/** Customers → Import: Shopify's customer CSV. Checked first; nothing is written until "Import customers". */
export function CustomerImportDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [csv, setCsv] = useState<string | null>(null)
  const [name, setName] = useState("")
  const [overwrite, setOverwrite] = useState(false)
  const [report, setReport] = useState<Result | null>(null)
  const run = useMutation({
    mutationFn: (v: { csv: string; dry_run: boolean; overwrite: boolean }) => sdk.client.fetch<Result>("/admin/terify/customers/import", { method: "POST", body: v }),
    onSuccess: (r) => {
      setReport(r)
      if (!r.dry_run) {
        qc.invalidateQueries({ queryKey: ["customers"] })
        if (!r.errors.length) toast.success(`${r.created} created, ${r.updated} updated`)
      }
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const check = (text: string, ow: boolean) => run.mutate({ csv: text, dry_run: true, overwrite: ow })
  const pick = async (file: File | undefined) => {
    setReport(null)
    if (!file) return
    if (file.size > MAX_BYTES) return toast.error("That file is too large (6 MB max). Split it into smaller files.")
    const text = await file.text()
    setCsv(text)
    setName(file.name)
    check(text, overwrite)
  }
  const template = () => {
    const a = document.createElement("a")
    a.href = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }))
    a.download = "customer_template.csv"
    a.click()
    URL.revokeObjectURL(a.href)
  }
  const ready = !!report && report.dry_run && !report.errors.length && report.created + report.updated > 0
  const done = !!report && !report.dry_run && !report.errors.length
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Import customers">
      <div className="my-8 w-full max-w-xl rounded-xl bg-ui-bg-base shadow-xl">
        <div className="border-b border-ui-border-base px-5 py-4 text-[15px] font-semibold">Import customers by CSV</div>
        <div className="flex flex-col gap-3 p-5">
          <Text size="small" className="text-ui-fg-subtle">
            Upload Shopify's customer export as it is, or use the{" "}
            <button type="button" className="text-ui-fg-interactive underline" onClick={template}>sample CSV template</button>. Only Email is required. Any error stops the whole import.
          </Text>
          <div className="flex items-center gap-3">
            <input ref={input} type="file" accept=".csv,text/csv" className="sr-only" aria-label="CSV file" onChange={(e) => pick(e.target.files?.[0])} />
            <Button size="small" variant="secondary" onClick={() => input.current?.click()}>{csv ? "Choose another file" : "Choose CSV file"}</Button>
            <Text size="small" className="truncate text-ui-fg-subtle">{name}</Text>
          </div>
          <label className="flex items-start gap-2 text-[13px]">
            <Checkbox
              checked={overwrite}
              onCheckedChange={(c) => {
                setOverwrite(c === true)
                if (csv) check(csv, c === true)
              }}
            />
            <span>
              Overwrite existing customers that have the same email
              <span className="block text-[12px] text-ui-fg-subtle">Updates name, phone, marketing, tags and note. Off: existing customers are skipped.</span>
            </span>
          </label>
          {run.isPending && <Text size="small">{report?.dry_run === false || (report && !run.variables?.dry_run) ? "Importing…" : "Checking file…"}</Text>}
          {report && (
            <div className="rounded-lg bg-ui-bg-subtle p-3 text-[13px]" aria-live="polite">
              <div className="font-semibold">
                {report.dry_run ? `${report.rows} row${report.rows === 1 ? "" : "s"}: ${report.created} new, ${report.updated} to update` : `${report.created} created, ${report.updated} updated`}
                {report.skipped > 0 && ` · ${report.skipped} already exist (skipped)`}
              </div>
              {report.errors.length > 0 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-ui-fg-error">
                  {report.errors.slice(0, 25).map((e, i) => <li key={i}>{e.line ? `Line ${e.line}: ` : ""}{e.message}</li>)}
                  {report.errors.length > 25 && <li>…and {report.errors.length - 25} more</li>}
                </ul>
              )}
              {report.warnings.length > 0 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-5 text-ui-fg-subtle">
                  {report.warnings.slice(0, 10).map((e, i) => <li key={i}>{e.line ? `Line ${e.line}: ` : ""}{e.message}</li>)}
                  {report.warnings.length > 10 && <li>…and {report.warnings.length - 10} more</li>}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-ui-border-base px-5 py-3">
          <Button variant="secondary" size="small" onClick={onClose}>{done ? "Close" : "Cancel"}</Button>
          {!done && <Button size="small" disabled={!ready} isLoading={run.isPending && !!report} onClick={() => csv && run.mutate({ csv, dry_run: false, overwrite })}>Import customers</Button>}
        </div>
      </div>
    </div>
  )
}
